import { CognitoUserPool } from 'amazon-cognito-identity-js'
import toast from 'react-hot-toast'

const API_BASE = import.meta.env.VITE_API_GATEWAY_URL || 'http://localhost:3001/api'

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
})

async function getToken(): Promise<string | null> {
  const msToken = localStorage.getItem('trc_ms_id_token')
  if (msToken) return msToken

  const cognitoUser = userPool.getCurrentUser()
  if (!cognitoUser) return null

  return new Promise((resolve) => {
    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session?.isValid()) resolve(null)
      else resolve(session.getIdToken().getJwtToken())
    })
  })
}

async function apiRequest<T = any>(path: string, options: RequestInit = {}): Promise<{ data: T; status: number }> {
  const token = await getToken()
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const resp = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (resp.status === 401) {
    toast.error('Session expired — please sign in again')
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }

  const data = await resp.json()
  return { data, status: resp.status }
}

// Convenience wrappers matching the old axios-like interface
export const apiClient = {
  get: <T = any>(path: string) => apiRequest<T>(path),
  post: <T = any>(path: string, body?: any) => apiRequest<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T = any>(path: string, body?: any) => apiRequest<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T = any>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
}

// Ticket-specific API helpers (used by pages)
export const ticketsApi = {
  getTickets: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return apiClient.get(`/tickets${qs}`)
  },
  getTicket: (ticketId: string) => apiClient.get(`/tickets/${ticketId}`),
  createTicket: (data: any) => apiClient.post('/tickets', data),
  updateTicket: (ticketId: string, data: any) => apiClient.put(`/tickets/${ticketId}`, data),
  assignTicket: (ticketId: string, assignedTo: string) => apiClient.post(`/tickets/${ticketId}/assign`, { assignedTo }),
  getComments: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/comments`),
  addComment: (ticketId: string, data: any) => apiClient.post(`/tickets/${ticketId}/comments`, data),
  getHistory: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/history`),
  getStats: () => apiClient.get('/tickets/stats'),
  triggerRCA: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/rca/trigger`),
  getRCA: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/rca`),
  approveRCA: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/rca/approve`),
  rejectRCA: (ticketId: string, reason?: string) => apiClient.post(`/tickets/${ticketId}/rca/reject`, { reason }),
  getTimeEntries: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/time`),
  addTimeEntry: (ticketId: string, data: any) => apiClient.post(`/tickets/${ticketId}/time`, data),
  startTimer: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/time/start`),
  stopTimer: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/time/stop`),
  getAuditTrail: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/audit`),
  getExecutions: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/executions`),
}

export const dashboardApi = {
  getDashboard: () => apiClient.get('/dashboard'),
  getMyTickets: () => apiClient.get('/dashboard/my-tickets'),
  getSLAReport: () => apiClient.get('/dashboard/sla-report'),
  getWorkload: () => apiClient.get('/dashboard/workload'),
}

export const usersApi = {
  getUsers: () => apiClient.get('/users'),
  getUser: (userId: string) => apiClient.get(`/users/${userId}`),
  createUser: (data: any) => apiClient.post('/users', data),
  updateUser: (userId: string, data: any) => apiClient.put(`/users/${userId}`, data),
  deleteUser: (userId: string) => apiClient.delete(`/users/${userId}`),
  getProfile: () => apiClient.get('/users/me'),
}

export const categoriesApi = {
  getCategories: () => apiClient.get('/categories'),
  createCategory: (data: any) => apiClient.post('/categories', data),
  updateCategory: (catId: string, data: any) => apiClient.put(`/categories/${catId}`, data),
  deleteCategory: (catId: string) => apiClient.delete(`/categories/${catId}`),
}
